import { valueOf } from '../template/document'
import { field, type FormTemplate, type Run } from '../template/types'

/**
 * Turns a filled-in template into positioned draw commands, one list per page.
 *
 * Deliberately free of pdf-lib: this is the half that can be wrong (where pages
 * break, whether a long answer is clipped, whether a signature block fits) and
 * keeping it pure means it can be tested with a stub text measurer instead of a
 * PDF backend. `render.ts` turns these commands into a document and does no
 * layout of its own.
 *
 * Geometry mirrors `ReleasePdf.kt`: US Letter in points, same margins, same type
 * sizes, so the two clients produce recognisably the same document.
 */

export const PAGE_W = 612
export const PAGE_H = 792
export const MARGIN_LEFT = 56
export const MARGIN_RIGHT = 56
export const MARGIN_TOP = 56
export const MARGIN_BOTTOM = 56

const LABEL_HEIGHT = 12
const SIG_BOX_W = 260
const SIG_BOX_H = 56

export type Style = 'regular' | 'bold' | 'italic' | 'boldItalic' | 'sans'
export type Ink = 'ink' | 'soft'

/** All coordinates measure down from the top-left of the page. */
export type DrawOp =
  | { op: 'text'; x: number; baseline: number; size: number; style: Style; color: Ink; text: string }
  | { op: 'line'; x1: number; x2: number; y: number; thickness: number; color: Ink }
  | { op: 'image'; key: string; x: number; top: number; width: number; height: number }

export interface Measurer {
  /** Width of `text` in points at `size`, in the given style. */
  width(text: string, style: Style, size: number): number
  /** Distance from the top of a line box to its baseline. */
  ascent(style: Style, size: number): number
  /** Full line height. */
  lineHeight(style: Style, size: number): number
}

export interface PdfMeta {
  signerName: string
  signedAtDisplay: string
  timeZone: string
  device: string
  appVersion: string
  templateId: string
  templateVersion: number
  fingerprint: string
  readToEnd: boolean
  /** The confirmation the signer had to tick, recorded verbatim. */
  agreed: string
}

export interface SignatureImage {
  width: number
  height: number
}

interface TextSpec {
  size: number
  style: Style
  color: Ink
}

const BODY: TextSpec = { size: 10, style: 'regular', color: 'ink' }
const NOTE: TextSpec = { size: 9, style: 'italic', color: 'soft' }
const H1: TextSpec = { size: 15, style: 'bold', color: 'ink' }
const H2: TextSpec = { size: 12.5, style: 'bold', color: 'ink' }
const H3: TextSpec = { size: 11, style: 'bold', color: 'ink' }
const LABEL: TextSpec = { size: 7.5, style: 'sans', color: 'soft' }
const FOOTER: TextSpec = { size: 7.5, style: 'sans', color: 'soft' }
const AUDIT: TextSpec = { size: 7.5, style: 'sans', color: 'soft' }

/** A word carrying the style it inherited from its run. */
interface Word {
  text: string
  style: Style
}

interface WrappedLine {
  words: Word[]
  width: number
}

export function layoutDocument(
  template: FormTemplate,
  values: Record<string, string>,
  signatures: Record<string, SignatureImage>,
  meta: PdfMeta,
  measure: Measurer,
): DrawOp[][] {
  const pages: DrawOp[][] = []
  let current: DrawOp[] = []
  let y = MARGIN_TOP

  const contentWidth = PAGE_W - MARGIN_LEFT - MARGIN_RIGHT
  const contentBottom = PAGE_H - MARGIN_BOTTOM

  const atPageTop = () => y <= MARGIN_TOP + 0.5

  // The first page is opened up front so a document is never zero pages.
  pages.push(current)

  const openNextPage = () => {
    current = []
    pages.push(current)
    y = MARGIN_TOP
  }

  const ensure = (height: number) => {
    if (y + height > contentBottom) openNextPage()
  }

  /** Draws wrapped lines, breaking across pages at line boundaries. */
  const drawLines = (lines: WrappedLine[], spec: TextSpec, x: number, centred = false) => {
    const height = measure.lineHeight(spec.style, spec.size)
    const ascent = measure.ascent(spec.style, spec.size)
    for (const line of lines) {
      if (y + height > contentBottom) {
        // Not even one line fits. On a fresh page that would loop forever, so
        // let the line overrun slightly rather than spin.
        if (!atPageTop()) openNextPage()
      }
      let cursor = centred ? x + (contentWidth - line.width) / 2 : x
      for (const word of line.words) {
        current.push({
          op: 'text',
          x: cursor,
          baseline: y + ascent,
          size: spec.size,
          style: word.style,
          color: spec.color,
          text: word.text,
        })
        cursor += measure.width(word.text, word.style, spec.size)
        cursor += measure.width(' ', word.style, spec.size)
      }
      y += height
    }
  }

  const paragraph = (runs: Run[], spec: TextSpec, indent = 0, centred = false) => {
    const words = wordsFromRuns(runs, values, spec.style)
    const lines = wrap(words, spec, contentWidth - indent, measure)
    drawLines(lines, spec, MARGIN_LEFT + indent, centred)
  }

  for (const block of template.body) {
    switch (block.kind) {
      case 'heading': {
        const spec = block.level === 1 ? H1 : block.level === 2 ? H2 : H3
        const spaceBefore = block.level === 1 ? 0 : block.level === 2 ? 16 : 12
        if (!atPageTop()) y += spaceBefore
        paragraph(block.runs, spec, 0, block.level === 1)
        y += 6
        break
      }
      case 'paragraph':
        paragraph(block.runs, BODY)
        y += 8
        break
      case 'note':
        paragraph(block.runs, NOTE, 16)
        y += 8
        break
      case 'bullet': {
        ensure(measure.lineHeight(BODY.style, BODY.size))
        current.push({
          op: 'text',
          x: MARGIN_LEFT,
          baseline: y + measure.ascent(BODY.style, BODY.size),
          size: BODY.size,
          style: 'regular',
          color: 'ink',
          text: block.marker,
        })
        paragraph(block.runs, BODY, 14)
        y += 4
        break
      }
      case 'rule':
        ensure(14)
        y += 6
        current.push({ op: 'line', x1: MARGIN_LEFT, x2: PAGE_W - MARGIN_RIGHT, y, thickness: 0.6, color: 'soft' })
        y += 10
        break
      case 'fieldLine': {
        const label = (field(template, block.key)?.label ?? block.key).toUpperCase()
        const text = valueOf(values, block.key)
        const words = wordsFromRuns([{ text, bold: false, italic: false }], values, BODY.style)
        const lines = wrap(words, BODY, contentWidth, measure)
        // Label, value and the rule under it stay together on one page.
        ensure(LABEL_HEIGHT + lines.length * measure.lineHeight(BODY.style, BODY.size) + 16)
        current.push({
          op: 'text',
          x: MARGIN_LEFT,
          baseline: y + measure.ascent(LABEL.style, LABEL.size),
          size: LABEL.size,
          style: LABEL.style,
          color: LABEL.color,
          text: label,
        })
        y += LABEL_HEIGHT
        drawLines(lines, BODY, MARGIN_LEFT)
        y += 3
        current.push({ op: 'line', x1: MARGIN_LEFT, x2: PAGE_W - MARGIN_RIGHT, y, thickness: 0.6, color: 'soft' })
        y += 12
        break
      }
      case 'signature': {
        const spec = template.signatures.find((s) => s.key === block.key)
        const image = signatures[block.key]
        // An optional signature nobody signed is left out rather than printed
        // as an empty box.
        if (!image && spec && !spec.required) break

        ensure(LABEL_HEIGHT + SIG_BOX_H + 18)
        current.push({
          op: 'text',
          x: MARGIN_LEFT,
          baseline: y + measure.ascent(LABEL.style, LABEL.size),
          size: LABEL.size,
          style: LABEL.style,
          color: LABEL.color,
          text: (spec?.label ?? block.key).toUpperCase(),
        })
        y += LABEL_HEIGHT
        if (image) {
          const scale = Math.min(SIG_BOX_W / image.width, SIG_BOX_H / image.height, 1)
          const w = image.width * scale
          const h = image.height * scale
          current.push({
            op: 'image',
            key: block.key,
            x: MARGIN_LEFT,
            top: y + (SIG_BOX_H - h),
            width: w,
            height: h,
          })
        }
        y += SIG_BOX_H + 2
        current.push({ op: 'line', x1: MARGIN_LEFT, x2: MARGIN_LEFT + SIG_BOX_W, y, thickness: 0.9, color: 'ink' })
        y += 14
        break
      }
    }
  }

  // Audit block, on whatever page is current when the body runs out.
  const auditText = [
    `Signed electronically on ${meta.signedAtDisplay} (${meta.timeZone}).`,
    `Captured on ${meta.device} using Consent ${meta.appVersion}. Template ${meta.templateId} v${meta.templateVersion}.`,
    meta.readToEnd
      ? 'The signer scrolled the full document before signing.'
      : 'The document was presented in full on screen; the signer did not scroll to the end before signing.',
    // Ticking this is mandatory, so the document should say what was ticked.
    // A consent record that does not record the consent is half a record.
    `Before signing, the signer confirmed: "${meta.agreed}"`,
    `Document fingerprint (SHA-256): ${meta.fingerprint}`,
  ]
  const auditLines = auditText.flatMap((line) =>
    wrap(wordsFromRuns([{ text: line, bold: false, italic: false }], values, AUDIT.style), AUDIT, contentWidth, measure),
  )
  ensure(auditLines.length * measure.lineHeight(AUDIT.style, AUDIT.size) + 22)
  y += 10
  current.push({ op: 'line', x1: MARGIN_LEFT, x2: PAGE_W - MARGIN_RIGHT, y, thickness: 0.6, color: 'soft' })
  y += 10
  drawLines(auditLines, AUDIT, MARGIN_LEFT)

  // Footers last, now that the total is known. Laying the body out first and
  // stamping "Page 1 of 5" afterwards means the count cannot disagree with the
  // pages actually produced, which is the failure the Android renderer needs
  // two passes to rule out.
  const total = pages.length
  pages.forEach((ops, index) => {
    const baseline = PAGE_H - MARGIN_BOTTOM + 22
    ops.push({ op: 'line', x1: MARGIN_LEFT, x2: PAGE_W - MARGIN_RIGHT, y: baseline - 12, thickness: 0.6, color: 'soft' })
    const left = `${template.title} - ${meta.signerName.trim() || 'unsigned'}`
    ops.push({
      op: 'text',
      x: MARGIN_LEFT,
      baseline,
      size: FOOTER.size,
      style: FOOTER.style,
      color: FOOTER.color,
      text: left,
    })
    const right = `Page ${index + 1} of ${total}`
    ops.push({
      op: 'text',
      x: PAGE_W - MARGIN_RIGHT - measure.width(right, FOOTER.style, FOOTER.size),
      baseline,
      size: FOOTER.size,
      style: FOOTER.style,
      color: FOOTER.color,
      text: right,
    })
  })

  return pages
}

/**
 * Flattens runs to words, substituting placeholder values.
 *
 * Words rather than whole runs because a line can mix styles, and wrapping has
 * to be able to break between any two words regardless of which run they came
 * from.
 */
function wordsFromRuns(runs: Run[], values: Record<string, string>, base: Style): Word[] {
  const words: Word[] = []
  for (const run of runs) {
    const text = run.placeholder ? valueOf(values, run.placeholder) : run.text
    // A placeholder always reads as bold so a filled-in value stands out from
    // the boilerplate around it, matching the on-screen document.
    const bold = run.bold || Boolean(run.placeholder)
    const style = styleFor(base, bold, run.italic)
    for (const piece of text.split(/\s+/)) {
      if (piece) words.push({ text: piece, style })
    }
  }
  return words
}

function styleFor(base: Style, bold: boolean, italic: boolean): Style {
  if (base === 'sans') return 'sans'
  if (bold && italic) return 'boldItalic'
  if (bold) return 'bold'
  if (italic) return 'italic'
  return base
}

function wrap(words: Word[], spec: TextSpec, maxWidth: number, measure: Measurer): WrappedLine[] {
  const lines: WrappedLine[] = []
  let currentWords: Word[] = []
  let width = 0

  const push = () => {
    if (currentWords.length) lines.push({ words: currentWords, width })
    currentWords = []
    width = 0
  }

  for (const word of words) {
    const wordWidth = measure.width(word.text, word.style, spec.size)
    const spaceWidth = currentWords.length ? measure.width(' ', word.style, spec.size) : 0
    if (currentWords.length && width + spaceWidth + wordWidth > maxWidth) {
      push()
      currentWords = [word]
      width = wordWidth
    } else {
      currentWords.push(word)
      width += spaceWidth + wordWidth
    }
  }
  push()
  // An empty value must still occupy a line, or the rule under a field line
  // would ride up into the label.
  return lines.length ? lines : [{ words: [], width: 0 }]
}
